// --- UPDATED FILE: packages/api-client/src/services/admin/adminSprintService.ts ---

import { getApiClient } from '../../client';
import { handleApiError } from '../../utils/handleApiError';
import type { 
    ServiceResponse,
    ProjectTask,
    Sprint, 
    SprintDay,
    SprintVersion,
    ChecklistItem,
    DayContentUpdatePayload, // Assuming this is defined in types/sprint.ts
    RevertPayload,
    UUID,
} from '@dopomogai/types';

// The base URL prefixes for different sprint-related resource groups.
const ADMIN_PROJECTS_PREFIX = '/admin/projects';
const ADMIN_SPRINTS_PREFIX = '/admin/sprints';
const ADMIN_DAYS_PREFIX = '/admin/days'; // New prefix for day-specific actions

/**
 * A collection of functions for performing administrative operations on Sprint-related
 * entities. These endpoints require admin authentication.
 */
export const adminSprintService = {
  // =================================================================
  // ==                      SPRINT ENTITY CRUD                     ==
  // =================================================================

  /** Creates a new sprint within a specific project. */
  createSprint: (projectId: UUID, data: Omit<Sprint, 'id' | 'project_id' | 'version'>): ServiceResponse<Sprint> => {
    return getApiClient().post<Sprint>(`${ADMIN_SPRINTS_PREFIX}/projects/${projectId}/sprints`, data)
      .then(res => res.data)
      .catch(err => handleApiError(err, `creating sprint in project ${projectId}`));
  },

  /** Updates an existing sprint's top-level details (name, goal, status, dates). */
  updateSprint: (sprintId: UUID, data: Sprint): ServiceResponse<Sprint> => {
    return getApiClient().put<Sprint>(`${ADMIN_SPRINTS_PREFIX}/${sprintId}`, data)
      .then(res => res.data)
      .catch(err => handleApiError(err, `updating sprint ${sprintId}`));
  },

   /** Deletes an entire sprint. This is a destructive action that un-assigns tasks. */
   deleteSprint: (sprintId: UUID): ServiceResponse<void> => {
    return getApiClient().delete<void>(`${ADMIN_SPRINTS_PREFIX}/${sprintId}`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `deleting sprint ${sprintId}`));
  },

  // =================================================================
  // ==                 SPRINT-TASK RELATIONSHIP                    ==
  // =================================================================

  /** Assigns an existing task from a project's global backlog to a sprint. */
  assignTaskToSprint: (sprintId: UUID, taskId: string): ServiceResponse<ProjectTask> => {
    return getApiClient().post<ProjectTask>(`${ADMIN_SPRINTS_PREFIX}/${sprintId}/assign-task/${taskId}`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `assigning task ${taskId} to sprint ${sprintId}`));
  },
  
  /** Un-assigns a task from its sprint, returning it to the project's global backlog. */
  unassignTaskFromSprint: (taskId: string): ServiceResponse<ProjectTask> => {
    return getApiClient().post<ProjectTask>(`${ADMIN_SPRINTS_PREFIX}/unassign-task/${taskId}`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `un-assigning task ${taskId} from its sprint`));
  },

  // =================================================================
  // ==                       DAY MANAGEMENT                        ==
  // =================================================================

  /** Triggers the backend to generate missing day records for a sprint's date range. */
  scaffoldDays: (sprintId: UUID): ServiceResponse<SprintDay[]> => {
    return getApiClient().post<SprintDay[]>(`${ADMIN_SPRINTS_PREFIX}/days/by-sprint/${sprintId}/scaffold`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `scaffolding days for sprint ${sprintId}`));
  },

  /** Updates the details of a specific day (e.g., summary, checklist). */
  updateDay: (dayId: UUID, data: DayContentUpdatePayload): ServiceResponse<SprintDay> => {
    return getApiClient().put<SprintDay>(`${ADMIN_SPRINTS_PREFIX}/days/${dayId}`, data)
      .then(res => res.data)
      .catch(err => handleApiError(err, `updating day ${dayId}`));
  },

  /** Deletes a day from a sprint. */
  deleteDay: (dayId: UUID): ServiceResponse<void> => {
    return getApiClient().delete<void>(`${ADMIN_SPRINTS_PREFIX}/days/${dayId}`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `deleting day ${dayId}`));
  },
  
  /** Assigns a task to a specific day's plan for visual organization. */
  assignTaskToDay: (dayId: UUID, taskId: string): ServiceResponse<void> => {
    return getApiClient().post(`${ADMIN_SPRINTS_PREFIX}/days/${dayId}/tasks/${taskId}/assign`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `assigning task ${taskId} to day ${dayId}`));
  },

  /** Un-assigns a task from a specific day's plan. */
  unassignTaskFromDay: (dayId: UUID, taskId: string): ServiceResponse<void> => {
    return getApiClient().post(`${ADMIN_SPRINTS_PREFIX}/days/${dayId}/tasks/${taskId}/unassign`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `un-assigning task ${taskId} from day ${dayId}`));
  },

  // =================================================================
  // ==                 SPRINT VERSIONING & REVERT                  ==
  // =================================================================

  /** Retrieves the version history (commit log) for an entire sprint. */
  listSprintVersions: (sprintId: UUID): ServiceResponse<SprintVersion[]> => {
    return getApiClient().get<SprintVersion[]>(`${ADMIN_SPRINTS_PREFIX}/${sprintId}/versions`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `listing sprint versions for ${sprintId}`));
  },

  /** Reverts the entire sprint to a previous version's state. */
  revertSprint: (sprintId: UUID, payload: RevertPayload): ServiceResponse<any> => {
    return getApiClient().post(`${ADMIN_SPRINTS_PREFIX}/${sprintId}/revert`, payload)
      .then(res => res.data)
      .catch(err => handleApiError(err, `reverting sprint ${sprintId}`));
  }
};